import { motion } from "framer-motion";
import { useIsMobile, reveal } from "@/lib/motion";
import { PHI, TYPE, GLOW, glassReveal } from "@/lib/glass";
import { smarten } from "@/lib/typography";
import BunnyVideo from "@/components/living-light/BunnyVideo";

const REEL = "e2a7c13e-0ebe-4c30-aa0e-95cbbc3c4b00";

// A closing testament — placed beneath everything, after "The work has only just begun."
const BODY = [
  "Everything you have seen here was made by an improbability.",
  "By someone who was never meant to make anything at all — the son of a mother who could not read or write; a child of poverty, and of hands that should have been gentle and were not; a foster boy passed between rooms that were never quite home; a young man who lost half a decade to a cell and the long company of his own mind. A Texas outlaw. And somewhere down in all of it, a stubborn and tender heart that simply grew tired — tired of watching good people carry more than their share while the world looked politely away.",
  "So I gathered fifteen years of quiet, honest devotion to a single craft and drew it back like an arrow on a bow of pure potential — not to wound anything, but to reach further than I was ever meant to reach, and to leave a wake of goodness wide enough for strangers to travel in.",
  "To whatever light carried me here — call it God, call it grace, call it the kindness of people who had no reason to be kind — thank you. For the hardship and the hard head. For the clarity that only comes from long nights. For turning old wounds into a map that might spare someone else the walk.",
  "And if you are reading this, and the world has already told you what you are — and it was small, and it was cruel, and it was wrong — then hear me. I am Charles Buerger. Nobody, from nowhere. If a life like mine can be lifted off the floor and aimed at something beautiful, so can yours. I am not special. I am only the proof.",
];

const bodyStyle = {
  marginBottom: PHI.s3,
  fontSize: TYPE.body,
  lineHeight: 1.85,
  letterSpacing: "0.02em",
  textShadow: GLOW.silverSoft,
};

export default function ClosingTestament() {
  const mobile = useIsMobile();

  return (
    <>
      <section
        data-testid="closing-reel-section"
        style={{ paddingLeft: PHI.s3, paddingRight: PHI.s3, paddingTop: PHI.s5, paddingBottom: PHI.s5 }}
      >
        <motion.div
          data-testid="closing-reel"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mx-auto w-full max-w-[960px]"
        >
          <BunnyVideo testId="video-testament" videoId={REEL} soundToggle playThreshold={0.3} revealDelay={200} />
        </motion.div>
      </section>

      <footer
        data-testid="testament-footer"
        style={{ paddingLeft: PHI.s3, paddingRight: PHI.s3, paddingTop: PHI.s5, paddingBottom: PHI.s6 }}
      >
        <div className="mx-auto max-w-[47rem]">
          <div aria-hidden="true" className="gold-hairline" style={{ marginBottom: PHI.s5 }} />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            {...glassReveal(reveal(mobile, { duration: 1.1 }))}
          >
            <p
              style={{ marginBottom: PHI.s4, fontSize: TYPE.lead, letterSpacing: "0.1em" }}
              className="font-display font-normal text-[#84DFF4]"
            >
              A Closing Word
            </p>
            {BODY.map((t, i) => (
              <p key={i} style={bodyStyle} className="measure font-light text-[#F5F5F7]">
                {smarten(t)}
              </p>
            ))}
            <p
              data-testid="testament-charge"
              style={{ marginTop: PHI.s3, marginBottom: PHI.s3, fontSize: TYPE.lead, lineHeight: 1.6, letterSpacing: "0.03em", textShadow: GLOW.gold }}
              className="measure font-light italic text-[#84DFF4]"
            >
              {smarten("Dream anyway. Build anyway. Become the light they swore you would never be.")}
            </p>
            <p
              data-testid="testament-signature"
              style={{ marginTop: PHI.s4, fontSize: TYPE.lead, letterSpacing: "0.04em", textShadow: GLOW.gold }}
              className="font-display font-light text-[#84DFF4]"
            >
              — Charles Buerger
            </p>
          </motion.div>
        </div>
      </footer>
    </>
  );
}
